import { normalizeDayNote } from '@platewell/core';
import { useCollections, useDebounced, useRecipesInList, type ListContext } from '@platewell/query';
import { useState } from 'react';
import { Dialog } from './dialog.js';

export interface RecipePickerDialogProps {
  /** What the recipe is being picked for, e.g. the day it will be planned on. */
  title: string;
  /** Recipes already chosen, left out of the list. */
  excludeIds?: ReadonlySet<string>;
  onPick: (recipeId: string) => void | Promise<void>;
  /**
   * Turns the search box into a note field too: whatever is typed can be added as plain
   * text from the first row, for a meal that is not a recipe.
   */
  onAddNote?: (text: string) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Rows drawn at once. A large household has thousands of recipes, and drawing a button for
 * each makes opening, switching lists and clearing the search visibly slow; nobody scrolls
 * that far rather than typing.
 */
const RESULT_LIMIT = 200;

type PickerList = Extract<
  ListContext,
  { kind: 'all' } | { kind: 'favourites' } | { kind: 'recent' } | { kind: 'collection' }
>;

function sameList(a: PickerList, b: PickerList): boolean {
  if (a.kind === 'collection' && b.kind === 'collection') {
    return a.collectionId === b.collectionId;
  }
  return a.kind === b.kind;
}

/**
 * A near full-height recipe chooser: a search box over a row of lists (All, Favourites,
 * Recently Added, then every collection), and the matching recipes below. The search runs
 * inside whichever list is selected. With `onAddNote`, the first row offers to add the search
 * text itself as a note.
 *
 * Mirrored on mobile by `apps/mobile/src/components/recipe-picker-sheet.tsx`; keep the two
 * in step.
 */
export function RecipePickerDialog({
  title,
  excludeIds,
  onPick,
  onAddNote,
  onClose,
}: RecipePickerDialogProps) {
  const collections = useCollections();
  const [list, setList] = useState<PickerList>({ kind: 'all' });
  const [rawTerm, setRawTerm] = useState('');
  const term = useDebounced(rawTerm, 150);
  // Not debounced: the row should echo exactly what is in the box.
  const note = onAddNote == null ? '' : normalizeDayNote(rawTerm);
  const recipes = useRecipesInList(list, term).filter(
    (recipe) => excludeIds == null || !excludeIds.has(recipe.id),
  );

  const lists: { key: string; label: string; list: PickerList }[] = [
    { key: 'all', label: 'All', list: { kind: 'all' } },
    { key: 'favourites', label: 'Favourites', list: { kind: 'favourites' } },
    { key: 'recent', label: 'Recent', list: { kind: 'recent' } },
    ...collections.map((collection) => ({
      key: collection.id,
      label: collection.name,
      list: { kind: 'collection', collectionId: collection.id } as const,
    })),
  ];

  return (
    <Dialog onClose={onClose} className="recipe-picker" aria-label={`Add a recipe to ${title}`}>
      <div className="row">
        <h2 style={{ margin: 0 }}>Add recipe</h2>
      </div>
      <p className="hint" style={{ margin: 0 }}>
        {title}
      </p>

      <input
        className="search-input"
        type="search"
        placeholder={onAddNote == null ? 'Search recipes…' : 'Search recipes or type a note…'}
        aria-label={onAddNote == null ? 'Search recipes' : 'Search recipes or type a note'}
        value={rawTerm}
        autoFocus
        onChange={(event) => setRawTerm(event.target.value)}
      />

      <div className="recipe-picker-lists" role="group" aria-label="Show recipes from">
        {lists.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className="recipe-picker-chip"
            aria-pressed={sameList(entry.list, list)}
            onClick={() => setList(entry.list)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <ul className="recipe-picker-results">
        {onAddNote != null && note !== '' && (
          <li>
            <button
              type="button"
              className="recipe-picker-result"
              onClick={() => void onAddNote(note)}
            >
              <span className="recipe-picker-note">
                <span className="recipe-picker-title">Add “{note}” as a note</span>
                <span className="hint">Shows on the day without linking to a recipe</span>
              </span>
              <span className="recipe-picker-add">Add</span>
            </button>
          </li>
        )}
        {recipes.slice(0, RESULT_LIMIT).map((recipe) => (
          <li key={recipe.id}>
            <button
              type="button"
              className="recipe-picker-result"
              onClick={() => void onPick(recipe.id)}
            >
              <span className="recipe-picker-title">{recipe.title}</span>
              <span className="recipe-picker-add">Add</span>
            </button>
          </li>
        ))}
        {recipes.length > RESULT_LIMIT && (
          <li className="hint recipe-picker-empty">
            Showing the first {RESULT_LIMIT} of {recipes.length}. Keep typing to narrow it down.
          </li>
        )}
        {recipes.length === 0 && (
          <li className="hint recipe-picker-empty">
            {term.trim() !== ''
              ? 'No matching recipes.'
              : list.kind === 'favourites'
                ? 'No favourites yet.'
                : 'No recipes here yet.'}
          </li>
        )}
      </ul>
    </Dialog>
  );
}
